import React, { FC } from 'react';
import BlockWrapper from '@components/common/BlockWrapper';
import InputLine from '@components/machines/components/detailsComponents/activeMachine/components/InputLine';
import ColorWrapper, { WithColor } from '@styles/colors';
import { MachineCountingProfile } from '@api/domain';
import { COUNTING_METHOD } from '@interfaces/machines';
import { Text, View } from 'react-native';
import { font } from '@styles/fonts';
import { scale } from '@components/frame';
import Svg from '@styles/icons/svg';
import { getMachineCountingMethodLabel } from '@components/settings/v2/structure/machines/machineDetails/helpers';
import { INPUT_LINE_HEIGHT, SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';
import { COUNTING_METHOD_BLOCK_CONFIG } from '@components/settings/v2/structure/machines/machines/machineCountSettings/constants';
import { injectConditionalObject } from '@helpers';
import { SmallLoading } from '@components/loading';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';
import i18n from 'i18n-js';

interface CountingMethodsBlockProps extends WithColor {
  title: string;
  countingMethods: NonNullable<MachineCountingProfile['countingMethods']>;
  loading: boolean;
  isSelectAutomatically: boolean | null;
  currentCountingMethod: COUNTING_METHOD | null;
  onCountingMethodChange: (countMethod: COUNTING_METHOD) => void;
  countingMethodText: string;
}

const CountingMethodsBlock: FC<CountingMethodsBlockProps> = ({
  C,
  title,
  countingMethods,
  isSelectAutomatically,
  loading,
  currentCountingMethod,
  onCountingMethodChange,
  countingMethodText,
}) => {
  const { iconHeight, iconWidth } = COUNTING_METHOD_BLOCK_CONFIG.nonAutomatic;

  const shouldConnectionMethodFieldBeClickable = countingMethods.length > 1;

  return (
    <BlockWrapper title={title}>
      {isSelectAutomatically === null && (
        <View testID={TEST_IDS.machineCountSettings.countingMethodsLoading} style={{ height: scale(INPUT_LINE_HEIGHT), justifyContent: 'center' }}>
          <SmallLoading />
        </View>
      )}
      {isSelectAutomatically === false && (
        <>
          {countingMethods.map((m, index) => (
            <InputLine
              {...{
                key: m.countingMethod,
                testID: TEST_IDS.machineCountSettings.countingMethodRow(m.countingMethod ?? ''),
                borderBottomRadius: index === countingMethods.length - 1,
                borderTopRadius: index === 0,
                data: countingMethods,
                onlyLabel: true,
                index,
                i: {
                  Component: () => (
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: scale(8) }}>
                      <View style={{ width: scale(iconWidth), height: scale(iconHeight) }}>
                        {m.countingMethod === currentCountingMethod && (
                          <Svg icon="check" color="Link/Light/Primary" width={scale(iconWidth)} height={scale(iconHeight)} />
                        )}
                      </View>
                      <Text style={[font['Body'], { color: C('Label/Light/Primary') }]}>
                        {i18n.t(m.countingMethod ? getMachineCountingMethodLabel(m.countingMethod as COUNTING_METHOD) : m.countingMethod || '')}
                      </Text>
                    </View>
                  ),
                  isText: false,
                  ...injectConditionalObject(shouldConnectionMethodFieldBeClickable, {
                    action: () => onCountingMethodChange(m.countingMethod as COUNTING_METHOD),
                  }),
                },
                C,
                loading,
              }}
            />
          ))}
        </>
      )}
      {isSelectAutomatically && (
        <View
          testID={TEST_IDS.machineCountSettings.automaticInfoBox}
          style={{
            backgroundColor: C('Background/Light/Field'),
            paddingVertical: scale(12),
            paddingHorizontal: scale(15),
            borderRadius: scale(SCREEN_BORDER_RADIUS),
            flexDirection: 'row',
            alignItems: 'flex-start',
            gap: scale(8),
          }}
        >
          <Svg icon="checkCircle" width={scale(20)} height={scale(20)} color="System/Light/Blue" />
          <Text testID={TEST_IDS.machineCountSettings.automaticInfoText} style={[font['Subhead 2'], { color: C('Label/Light/Primary'), flex: 1 }]}>
            {i18n.t(countingMethodText)}
          </Text>
        </View>
      )}
    </BlockWrapper>
  );
};

export default ColorWrapper(CountingMethodsBlock);
